import { createContext, useContext, useState, useCallback } from 'react';

const translations = {
  en: {
    nav: {
      dashboard: 'Dashboard',
      payment: 'New Payment',
      transactions: 'Transactions',
      analytics: 'Analytics',
      staff: 'Staff',
      settings: 'Settings',
      templates: 'Templates',
      inventory: 'Inventory',
      support: 'Support',
      reports: 'Reports',
      logout: 'Logout'
    },
    dashboard: {
      title: 'Dashboard',
      subtitle: "Today's overview",
      revenueToday: 'Revenue Today',
      transactions: 'Transactions',
      avgBasket: 'Avg Basket',
      recentTransactions: 'Recent Transactions',
      viewAll: 'View All',
      newPayment: 'New Payment',
      noTransactions: 'No transactions yet. Create your first payment!'
    },
    payment: {
      title: 'New Payment',
      subtitle: 'Enter amount and generate QR code',
      amount: 'Amount',
      description: 'Description (optional)',
      charge: 'Charge',
      creating: 'Creating...',
      scanToPay: 'Scan to Pay',
      waitingForPayment: 'Waiting for payment...',
      paymentReceived: 'Payment Received',
      paymentFailed: 'Payment Failed',
      sessionExpired: 'The payment session expired or was cancelled.',
      newPaymentBtn: 'New Payment',
      tryAgain: 'Try Again',
      cancel: 'Cancel'
    },
    transactions: {
      title: 'Transactions',
      totalTransactions: 'total transactions',
      allStatus: 'All Status',
      paid: 'Paid',
      pending: 'Pending',
      refunded: 'Refunded',
      failed: 'Failed',
      date: 'Date',
      descriptionCol: 'Description',
      amountCol: 'Amount',
      status: 'Status',
      action: 'Action',
      noTransactions: 'No transactions found',
      exportCsv: 'Export CSV',
      from: 'From',
      to: 'To',
      receipt: 'Receipt'
    },
    analytics: {
      title: 'Analytics',
      subtitle: 'Payment performance overview',
      today: 'Today',
      days7: '7 Days',
      days30: '30 Days',
      days90: '90 Days',
      revenue: 'Revenue',
      paidLabel: 'Paid',
      avgBasket: 'Avg Basket',
      refunds: 'Refunds',
      dailyRevenue: 'Daily Revenue',
      noData: 'No data for this period',
      statusBreakdown: 'Status Breakdown',
      transactionsLabel: 'transactions'
    },
    staff: {
      title: 'Staff',
      teamMembers: 'team members',
      addMember: 'Add Member',
      noStaff: 'No staff members yet',
      name: 'Name',
      email: 'Email',
      password: 'Password',
      role: 'Role',
      cashier: 'Cashier',
      admin: 'Admin',
      addStaffTitle: 'Add Staff Member',
      addStaffDesc: 'Create a new account for a team member.',
      cancelBtn: 'Cancel',
      adding: 'Adding...',
      removeTitle: 'Remove Staff Member',
      removeDesc: 'Are you sure you want to remove {name}? They will lose access to the store.',
      removing: 'Removing...',
      remove: 'Remove'
    },
    settings: {
      title: 'Store Settings',
      subtitle: 'Manage your store profile',
      storeProfile: 'Store Profile',
      storeName: 'Store Name',
      address: 'Address',
      phone: 'Phone',
      currency: 'Currency',
      language: 'Language',
      saving: 'Saving...',
      saveChanges: 'Save Changes'
    },
    receipt: {
      title: 'Payment Receipt',
      store: 'Store',
      date: 'Date',
      transactionId: 'Transaction ID',
      amount: 'Amount',
      status: 'Status',
      paidBy: 'Paid by',
      description: 'Description',
      print: 'Print Receipt',
      back: 'Back',
      thankYou: 'Thank you for your payment!',
      poweredBy: 'Powered by EmergencyPOS'
    },
    auth: {
      login: 'Login',
      signUp: 'Sign Up',
      email: 'Email',
      password: 'Password',
      fullName: 'Full Name',
      storeName: 'Store Name',
      signIn: 'Sign In',
      signingIn: 'Signing in...',
      createAccount: 'Create Account',
      creatingAccount: 'Creating account...',
      or: 'or',
      continueWithGoogle: 'Continue with Google',
      securePayments: 'Secure payments powered by Stripe. No card data stored.',
      tagline: 'Emergency payment solution for when your terminal is down'
    }
  },
  fr: {
    nav: {
      dashboard: 'Tableau de bord',
      payment: 'Nouveau paiement',
      transactions: 'Transactions',
      analytics: 'Analytique',
      staff: 'Personnel',
      settings: 'Paramètres',
      templates: 'Modèles',
      inventory: 'Inventaire',
      support: 'Support',
      reports: 'Rapports',
      logout: 'Déconnexion'
    },
    dashboard: {
      title: 'Tableau de bord',
      subtitle: "Aperçu du jour",
      revenueToday: "Chiffre d'affaires",
      transactions: 'Transactions',
      avgBasket: 'Panier moyen',
      recentTransactions: 'Transactions récentes',
      viewAll: 'Voir tout',
      newPayment: 'Nouveau paiement',
      noTransactions: 'Aucune transaction. Créez votre premier paiement !'
    },
    payment: {
      title: 'Nouveau paiement',
      subtitle: 'Entrez le montant et générez un QR code',
      amount: 'Montant',
      description: 'Description (optionnel)',
      charge: 'Encaisser',
      creating: 'Création...',
      scanToPay: 'Scanner pour payer',
      waitingForPayment: 'En attente du paiement...',
      paymentReceived: 'Paiement reçu',
      paymentFailed: 'Paiement échoué',
      sessionExpired: 'La session de paiement a expiré ou a été annulée.',
      newPaymentBtn: 'Nouveau paiement',
      tryAgain: 'Réessayer',
      cancel: 'Annuler'
    },
    transactions: {
      title: 'Transactions',
      totalTransactions: 'transactions au total',
      allStatus: 'Tous les statuts',
      paid: 'Payé',
      pending: 'En attente',
      refunded: 'Remboursé',
      failed: 'Échoué',
      date: 'Date',
      descriptionCol: 'Description',
      amountCol: 'Montant',
      status: 'Statut',
      action: 'Action',
      noTransactions: 'Aucune transaction trouvée',
      exportCsv: 'Exporter CSV',
      from: 'Du',
      to: 'Au',
      receipt: 'Reçu'
    },
    analytics: {
      title: 'Analytique',
      subtitle: 'Aperçu des performances de paiement',
      today: "Aujourd'hui",
      days7: '7 Jours',
      days30: '30 Jours',
      days90: '90 Jours',
      revenue: 'Revenus',
      paidLabel: 'Payés',
      avgBasket: 'Panier moyen',
      refunds: 'Remboursements',
      dailyRevenue: 'Revenus quotidiens',
      noData: 'Aucune donnée pour cette période',
      statusBreakdown: 'Répartition par statut',
      transactionsLabel: 'transactions'
    },
    staff: {
      title: 'Personnel',
      teamMembers: 'membres',
      addMember: 'Ajouter',
      noStaff: 'Aucun membre du personnel',
      name: 'Nom',
      email: 'Email',
      password: 'Mot de passe',
      role: 'Rôle',
      cashier: 'Caissier',
      admin: 'Administrateur',
      addStaffTitle: 'Ajouter un membre',
      addStaffDesc: 'Créez un nouveau compte pour un membre de l\'équipe.',
      cancelBtn: 'Annuler',
      adding: 'Ajout...',
      removeTitle: 'Supprimer un membre',
      removeDesc: 'Êtes-vous sûr de vouloir supprimer {name} ? Il perdra l\'accès au magasin.',
      removing: 'Suppression...',
      remove: 'Supprimer'
    },
    settings: {
      title: 'Paramètres',
      subtitle: 'Gérer le profil du magasin',
      storeProfile: 'Profil du magasin',
      storeName: 'Nom du magasin',
      address: 'Adresse',
      phone: 'Téléphone',
      currency: 'Devise',
      language: 'Langue',
      saving: 'Enregistrement...',
      saveChanges: 'Enregistrer'
    },
    receipt: {
      title: 'Reçu de paiement',
      store: 'Magasin',
      date: 'Date',
      transactionId: 'N° Transaction',
      amount: 'Montant',
      status: 'Statut',
      paidBy: 'Payé par',
      description: 'Description',
      print: 'Imprimer le reçu',
      back: 'Retour',
      thankYou: 'Merci pour votre paiement !',
      poweredBy: 'Propulsé par EmergencyPOS'
    },
    auth: {
      login: 'Connexion',
      signUp: 'Inscription',
      email: 'Email',
      password: 'Mot de passe',
      fullName: 'Nom complet',
      storeName: 'Nom du magasin',
      signIn: 'Se connecter',
      signingIn: 'Connexion...',
      createAccount: 'Créer un compte',
      creatingAccount: 'Création...',
      or: 'ou',
      continueWithGoogle: 'Continuer avec Google',
      securePayments: 'Paiements sécurisés par Stripe. Aucune donnée de carte stockée.',
      tagline: "Solution de paiement d'urgence quand votre terminal est en panne"
    }
  }
};

const LanguageContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem('epos_lang') || 'en';
    } catch {
      return 'en';
    }
  });

  const setLanguage = useCallback((newLang) => {
    setLang(newLang);
    try {
      localStorage.setItem('epos_lang', newLang);
    } catch {
      // ignore
    }
  }, []);

  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = translations[lang];
    for (const k of keys) {
      if (!value) return key;
      value = value[k];
    }
    return value || key;
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
}
